import { useState, useRef, useEffect } from "react";
import { useGetVendorsDropdown } from "hooks/useVendors";
import { useGetProductsDropdown } from "hooks/useProducts";
import { adminUploadProductVideo, vendorUploadProductVideo } from "services/productVideos";

const MAX_MB = 50;

/**
 * Upload modal shared by admin + vendor. Picks the right service call based
 * on `scope`. Admin must select a vendor first; vendor uploads always
 * scope to their own products.
 */
const ProductVideoUploadModal = ({ scope, currentUser, onClose, onUploaded }) => {
    const isAdminScope = scope === "admin";
    const { vendors } = useGetVendorsDropdown();

    const [vendorId, setVendorId] = useState(isAdminScope ? "" : (currentUser?.id ?? ""));
    const [productId, setProductId] = useState("");
    const [caption, setCaption] = useState("");
    const [progress, setProgress] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const videoRef = useRef(null);
    const thumbRef = useRef(null);

    // Pull the selected vendor's products via the dedicated dropdown
    // endpoint — returns ALL products (no pagination) with only the
    // fields the <select> needs. The old `getProducts({ vendorId,
    // limit: 200 })` silently cut vendors with bigger menus off at 200.
    // Disabled until a vendor is picked.
    const { products, loading: productsLoading } = useGetProductsDropdown(
        { vendorId },
        { enabled: Boolean(vendorId) },
    );

    // Reset selected product when vendor changes.
    useEffect(() => {
        setProductId("");
    }, [vendorId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        const videoFile = videoRef.current?.files?.[0];
        const thumbFile = thumbRef.current?.files?.[0];

        if (!productId) return setError("اختر المنتج");
        if (!videoFile) return setError("الفيديو مطلوب");
        if (videoFile.size > MAX_MB * 1024 * 1024) {
            return setError(`حجم الفيديو أكبر من ${MAX_MB} ميجابايت`);
        }

        const formData = new FormData();
        formData.append("video", videoFile);
        if (thumbFile) formData.append("thumbnail", thumbFile);
        formData.append("productId", String(productId));
        if (caption) formData.append("caption", caption);

        setSubmitting(true);
        try {
            const upload = isAdminScope ? adminUploadProductVideo : vendorUploadProductVideo;
            await upload(formData, {
                onUploadProgress: (e) => {
                    if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
                },
            });
            onUploaded?.();
            onClose();
        } catch (err) {
            setError(err.message || "فشل الرفع");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="product-video-modal-backdrop" onClick={onClose}>
            <div className="product-video-modal" onClick={(e) => e.stopPropagation()}>
                <header>
                    <h2>رفع فيديو لمنتج</h2>
                    <button type="button" className="close" onClick={onClose}>
                        ×
                    </button>
                </header>
                <form onSubmit={handleSubmit}>
                    {isAdminScope ? (
                        <label>
                            <span>المطعم</span>
                            <select
                                value={vendorId}
                                onChange={(e) => setVendorId(e.target.value)}
                                required
                            >
                                <option value="">— اختر المطعم —</option>
                                {vendors.map((v) => (
                                    <option key={v.id} value={v.id}>
                                        {v.name}
                                    </option>
                                ))}
                            </select>
                        </label>
                    ) : null}

                    <label>
                        <span>المنتج</span>
                        <select
                            value={productId}
                            onChange={(e) => setProductId(e.target.value)}
                            required
                            disabled={!vendorId || productsLoading}
                        >
                            <option value="">
                                {productsLoading
                                    ? "...جار التحميل"
                                    : !vendorId
                                      ? "اختر المطعم أولًا"
                                      : products.length === 0
                                        ? "لا توجد منتجات"
                                        : "— اختر المنتج —"}
                            </option>
                            {products.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.title}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label>
                        <span>فيديو (MP4 / MOV / WebM، حد أقصى {MAX_MB} ميجابايت)</span>
                        <input
                            type="file"
                            ref={videoRef}
                            accept="video/mp4,video/quicktime,video/webm"
                            required
                        />
                    </label>

                    <label>
                        <span>صورة الغلاف (اختيارية)</span>
                        <input
                            type="file"
                            ref={thumbRef}
                            accept="image/jpeg,image/png,image/webp"
                        />
                    </label>

                    <label>
                        <span>نص قصير (اختياري)</span>
                        <input
                            type="text"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            maxLength={500}
                            placeholder="مثال: حار وطازج ومش غالي"
                        />
                    </label>

                    {progress > 0 && progress < 100 ? (
                        <progress value={progress} max={100} />
                    ) : null}

                    {error ? <p className="error">{error}</p> : null}

                    <div className="actions">
                        <button type="button" onClick={onClose}>
                            إلغاء
                        </button>
                        <button type="submit" className="primary" disabled={submitting}>
                            {submitting ? "...جارٍ الرفع" : "رفع"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductVideoUploadModal;
